import { useState } from "react";
import { api, type AccessRequest } from "../api";
import { useAsync } from "../hooks";
import { fmt } from "../format";
import { ConfirmModal } from "./ConfirmModal";
import type { InvitesResponse } from "../types";

interface SettingsData {
  maintenanceMode: boolean;
  fullLockdown: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

/**
 * Platform-wide operator controls. Operator-only at the API too (403 for customers) — this
 * component is only reachable behind `me.admin`. Two maintenance tiers: tier 1 pauses NEW sign-ups;
 * tier 2 (full lockdown) blocks ALL non-operator access.
 */
export function Settings() {
  const { data, loading, error, reload } = useAsync<SettingsData>(() => api("/settings"), []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmPause, setConfirmPause] = useState(false);
  const [confirmLock, setConfirmLock] = useState(false);

  async function apply(patch: { maintenanceMode?: boolean; fullLockdown?: boolean }) {
    setBusy(true);
    setErr(null);
    try {
      await api("/settings", { method: "PATCH", body: JSON.stringify(patch) });
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "failed to change settings");
    } finally {
      setBusy(false);
    }
  }

  // Turning a control ON is the direction worth a second look; turning it OFF is harmless + immediate.
  function onTogglePause(next: boolean) {
    if (next) return setConfirmPause(true);
    void apply({ maintenanceMode: false });
  }
  function onToggleLock(next: boolean) {
    if (next) return setConfirmLock(true);
    void apply({ fullLockdown: false });
  }

  if (loading && !data) return <div className="muted pad">Loading…</div>;
  if (error) return <div className="err pad">Error: {error}</div>;
  if (!data) return null;

  const on = data.maintenanceMode;
  const locked = data.fullLockdown;

  return (
    <div className="stack">
      <div className="section-head">
        <h1>Settings</h1>
        <span className="muted">Platform-wide. Operator only.</span>
      </div>

      {/* Both maintenance tiers live in ONE card now — escalating rows, compact. The exhaustive
          "what this does" copy moved to each tier's ConfirmModal, where it's read at the moment it
          matters; an operator scanning the panel shouldn't have to wade through it every time. */}
      <section className="card">
        <div className="section-head">
          <div>
            <h2>Maintenance mode</h2>
            <p className="fleet-line" style={{ marginTop: 6 }}>
              Two escalating tiers. Operators and the admin break-glass key always get in, and
              background scraping keeps running either way.
            </p>
          </div>
        </div>

        {err && <div className="err mt">{err}</div>}

        {/* Per the operator's ask: just the title, the one-line description, and two self-describing
            buttons — no status pills, no per-tier labels, no "last changed" (that record lives in the
            Audit tab). Each button's label carries the current state; the full consequence is a hover
            title, and the ConfirmModal spells it out at the moment of action. */}
        <div className="row-gap" style={{ marginTop: "var(--sp4)", gap: "var(--sp3)" }}>
          <button
            className={on ? "btn" : "btn danger"}
            onClick={() => onTogglePause(!on)}
            disabled={busy}
            title={
              on
                ? "Reopen sign-ups — anyone can create an account again."
                : "Pause new sign-ups — existing users keep signing in and scrapers keep running; only brand-new accounts are blocked."
            }
          >
            {busy ? "Saving…" : on ? "Reopen sign-ups" : "Pause sign-ups"}
          </button>
          <button
            className={locked ? "btn" : "btn danger"}
            onClick={() => onToggleLock(!locked)}
            disabled={busy}
            title={
              locked
                ? "Lift the lockdown — restore normal access for everyone."
                : "Lock everything down — all non-operator access is blocked (existing users get a maintenance page, API keys 503). Operators and the admin key still get in; background scraping is unaffected."
            }
          >
            {busy ? "Saving…" : locked ? "Lift lockdown" : "Lock down"}
          </button>
        </div>
      </section>

      <BypassInvites paused={on} />

      <AccessRequests />

      {confirmPause && (
        <ConfirmModal
          title="Pause new sign-ups?"
          body="People who already have an account keep working normally, and every scraper keeps running. You can reopen sign-ups anytime."
          confirmLabel="Pause sign-ups"
          danger
          busy={busy}
          onCancel={() => setConfirmPause(false)}
          onConfirm={() => void apply({ maintenanceMode: true }).finally(() => setConfirmPause(false))}
        />
      )}

      {confirmLock && (
        <ConfirmModal
          title="Lock everything down?"
          body="This blocks ALL non-operator access — every existing user gets a maintenance page and API keys receive 503. Only operators and the admin key get in. Background scraping keeps running. You can lift the lockdown anytime."
          confirmLabel="Lock everything down"
          danger
          busy={busy}
          onCancel={() => setConfirmLock(false)}
          onConfirm={() => void apply({ fullLockdown: true }).finally(() => setConfirmLock(false))}
        />
      )}
    </div>
  );
}

/**
 * Access requests left by turned-away visitors (POST /public/access-request while a maintenance tier is
 * on). Operators invite (pre-create a bypass account so they can sign in during the freeze) or dismiss.
 */
function AccessRequests() {
  const { data, loading, reload } = useAsync<{ pending: AccessRequest[] }>(() => api("/settings/access-requests"), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const pending = data?.pending ?? [];

  async function act(id: string, kind: "invite" | "dismiss") {
    setBusy(id);
    setErr(null);
    try {
      await api(`/settings/access-requests/${id}/${kind}`, { method: "POST" });
      reload();
    } catch (x) {
      setErr(x instanceof Error ? x.message : `failed to ${kind}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="card">
      <div className="section-head">
        <div>
          <h2>Access requests</h2>
          <p className="fleet-line" style={{ marginTop: 6 }}>
            People who asked for access from the maintenance banner. <em>Invite</em> pre-creates an account
            they claim on first sign-in (bypassing the freeze); <em>Dismiss</em> clears the request.
          </p>
        </div>
      </div>
      {err && <div className="err mt">{err}</div>}
      {loading && !data ? (
        <div className="muted pad">Loading…</div>
      ) : pending.length === 0 ? (
        <div className="empty">No access requests waiting.</div>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Note</th>
                <th>Requested</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pending.map((r) => (
                <tr key={r.id}>
                  <td>{r.email}</td>
                  <td className="muted">{r.note || "—"}</td>
                  <td className="nowrap">{fmt(r.createdAt)}</td>
                  <td className="acts">
                    <button className="btn sm" disabled={busy !== null} onClick={() => void act(r.id, "invite")}>
                      {busy === r.id ? "…" : "Invite"}
                    </button>
                    <button className="btn ghost sm" disabled={busy !== null} onClick={() => void act(r.id, "dismiss")}>
                      Dismiss
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * Let specific people IN while sign-ups are paused (works anytime). Inviting an email pre-creates an
 * account the person claims on first Google login — which bypasses the freeze — and emails them.
 */
function BypassInvites({ paused }: { paused: boolean }) {
  const { data, loading, reload } = useAsync<InvitesResponse>(() => api("/settings/invites"), []);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const invites = data?.pending ?? [];
  const accepted = data?.accepted ?? [];

  async function invite() {
    const e = email.trim().toLowerCase();
    if (!e || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await api("/settings/invites", { method: "POST", body: JSON.stringify({ email: e }) });
      setEmail("");
      reload();
    } catch (x) {
      setErr(x instanceof Error ? x.message : "failed to invite");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    setBusy(true);
    setErr(null);
    try {
      await api(`/settings/invites/${id}`, { method: "DELETE" });
      reload();
    } catch (x) {
      setErr(x instanceof Error ? x.message : "failed to revoke");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <div className="section-head">
        <div>
          <h2>Let people in</h2>
          <p className="fleet-line" style={{ marginTop: 6 }}>
            Invite an email to <em>bypass the sign-up freeze</em>. They claim the account on first
            Google sign-in{paused ? "" : " — useful once sign-ups are paused"}.
          </p>
        </div>
      </div>

      <form
        className="row-gap"
        style={{ marginTop: "var(--sp4)" }}
        onSubmit={(ev) => {
          ev.preventDefault();
          void invite();
        }}
      >
        <input
          type="email"
          placeholder="person@company.com"
          value={email}
          disabled={busy}
          onChange={(ev) => setEmail(ev.target.value)}
          style={{ maxWidth: 300 }}
        />
        <button className="btn" type="submit" disabled={busy || !email.trim()}>
          {busy ? "Inviting…" : "Invite"}
        </button>
      </form>
      {err && <div className="err mt">{err}</div>}

      <h3 className="mt">Pending invites</h3>
      {loading && !data ? (
        <div className="muted pad">Loading…</div>
      ) : invites.length === 0 ? (
        <div className="empty">No pending invites — nobody is waiting to claim an account.</div>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Invited</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {invites.map((iv) => (
                <tr key={iv.id}>
                  <td>{iv.email}</td>
                  <td className="nowrap">{fmt(iv.createdAt)}</td>
                  <td className="acts">
                    <button className="btn ghost sm" disabled={busy} onClick={() => void revoke(iv.id)}>
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {accepted.length > 0 && (
        <>
          <h3 className="mt">Accepted recently</h3>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Accepted</th>
                </tr>
              </thead>
              <tbody>
                {accepted.map((a) => (
                  <tr key={a.id}>
                    <td>{a.email}</td>
                    <td className="nowrap">{fmt(a.claimedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
